import axios from 'axios';
import { getToken, setToken, removeToken, getRefreshToken, setRefreshToken, removeRefreshToken } from '../utils/storage';

const getBaseURL = () => {
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        return 'http://localhost:5000/api';
    }
    return process.env.REACT_APP_API_URL || '/api';
};

const instance = axios.create({
    baseURL: getBaseURL(),
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor
instance.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const REFRESH_URL = '/auth/refresh-token';

// A 401 from these endpoints is the answer, not an expired session. Refreshing
// and retrying here is pointless: it fires a second request, logs a second 401
// in the console, and can overwrite a good session with tokens minted for a
// login that just failed.
const NON_REFRESHABLE_PATHS = [
    '/auth/login',
    '/auth/refresh-token',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-email'
];

const isAuthEndpoint = (url = '') => NON_REFRESHABLE_PATHS.some((p) => url.includes(p));

const signOut = () => {
    removeToken();
    removeRefreshToken();
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

// Response interceptor
instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || !originalRequest) {
            return Promise.reject(error);
        }

        // Credential endpoints answer for themselves. A 401 from /auth/login
        // means the credentials were rejected — surface it untouched so the
        // login screen can show the server's message. A 401 from the refresh
        // endpoint means the refresh token is dead; retrying it here would
        // re-enter this interceptor and recurse forever.
        if (isAuthEndpoint(originalRequest.url)) {
            if (originalRequest.url.includes(REFRESH_URL)) {
                signOut();
            }
            return Promise.reject(error);
        }

        if (originalRequest._retry) {
            return Promise.reject(error);
        }
        originalRequest._retry = true;

        // No refresh token at all: previously this fell through silently and
        // left the user on a broken page instead of sending them to login.
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
            signOut();
            return Promise.reject(error);
        }

        try {
            const response = await instance.post(REFRESH_URL, { refreshToken });
            const { accessToken, refreshToken: newRefreshToken } = response.data.data;

            setToken(accessToken);
            setRefreshToken(newRefreshToken);

            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return instance(originalRequest);
        } catch (refreshError) {
            signOut();
            return Promise.reject(error);
        }
    }
);

export default instance;
